import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, AtSign, CheckCheck, Inbox, Bell, BellRing, Sparkles } from "lucide-react";
import {
  notificationPermission,
  requestNotificationPermission,
  supportsWebNotifications,
} from "@/utils/web-notifications";
import {
  acceptFriendRequest,
  denyFriendRequest,
  useNotificationStore,
} from "@slumbr/core";
import { useSlumbrNotifications } from "@slumbr/app-sdk";
import { cn } from "@slumbr/ui";

const NOTIF_BANNER_DISMISSED_KEY = "slumbr-notif-prompt-dismissed-at";
const NOTIF_BANNER_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function extractActor(notification: { metadata?: Record<string, unknown> }): {
  userId?: string;
  displayName?: string;
  avatarUrl?: string;
} | null {
  const actor = notification.metadata?.actor;
  if (!actor || typeof actor !== "object") return null;
  const record = actor as Record<string, unknown>;
  return {
    userId: typeof record.userId === "string" ? record.userId : undefined,
    displayName:
      typeof record.displayName === "string" ? record.displayName : undefined,
    avatarUrl:
      typeof record.avatarUrl === "string" ? record.avatarUrl : undefined,
  };
}

function formatRelative(iso: string) {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function NotificationPanel() {
  const isOpen = useNotificationStore((s) => s.isOpen);
  const notifications = useNotificationStore((s) => s.notifications);
  const setOpen = useNotificationStore((s) => s.setOpen);
  const { markAsRead, markAllAsRead } = useSlumbrNotifications();
  const [activeTab, setActiveTab] = useState<"for-you" | "mentions" | "general">("general");
  const [respondingRequestId, setRespondingRequestId] = useState<string | null>(
    null
  );
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(notificationPermission());
  // Persisted timestamp of the last time the user dismissed the "Enable
  // notifications" banner. The browser parks permission state in "default"
  // when the user closes the OS prompt with the X or clicks "Not now" —
  // without tracking dismissal, the banner reappears every panel open even
  // after they've clearly said no.
  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    try {
      const value = typeof window !== "undefined"
        ? window.localStorage.getItem(NOTIF_BANNER_DISMISSED_KEY)
        : null;
      return value ? Number.parseInt(value, 10) || 0 : 0;
    } catch {
      return 0;
    }
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    setPermission(notificationPermission());
  }, [isOpen]);

  async function enableNotifications() {
    const result = await requestNotificationPermission();
    setPermission(result);
    // User engaged with the banner — clear dismissal so the prompt-once
    // contract holds (we won't pester them again until they reset perms).
    try {
      window.localStorage.removeItem(NOTIF_BANNER_DISMISSED_KEY);
      setDismissedAt(0);
    } catch {
      // ignore
    }
  }

  function dismissBanner() {
    const stamp = Date.now();
    try {
      window.localStorage.setItem(NOTIF_BANNER_DISMISSED_KEY, String(stamp));
    } catch {
      // ignore quota errors
    }
    setDismissedAt(stamp);
  }

  // Show the banner only when the browser is genuinely in the "default"
  // state AND the user hasn't dismissed it within the snooze window. After
  // 14 days we re-prompt once — covers the case where a user wanted to opt
  // in later but forgot.
  const bannerSnoozed =
    dismissedAt > 0 && Date.now() - dismissedAt < NOTIF_BANNER_SNOOZE_MS;
  const showPermissionBanner =
    supportsWebNotifications() && permission === "default" && !bannerSnoozed;

  const { forYouNotifications, mentionsNotifications, generalNotifications } = useMemo(() => {
    const isMention = (notification: (typeof notifications)[number]) =>
      notification.type === "chat_mention" ||
      notification.type === "chat_everyone_mention" ||
      notification.type === "mention";
    const isForYou = (notification: (typeof notifications)[number]) =>
      notification.type === "friend_request" ||
      notification.metadata?.forYou === true;

    const byNewest = [...notifications].sort((a, b) => {
      const aMs = new Date(a.createdAt).getTime();
      const bMs = new Date(b.createdAt).getTime();
      return bMs - aMs;
    });
    const mentions = byNewest.filter(isMention);
    return {
      mentionsNotifications: mentions,
      forYouNotifications: byNewest.filter((notification) => !isMention(notification) && isForYou(notification)),
      generalNotifications: byNewest.filter((notification) => !isMention(notification) && !isForYou(notification)),
    };
  }, [notifications]);

  const visibleNotifications =
    activeTab === "for-you"
      ? forYouNotifications
      : activeTab === "mentions"
        ? mentionsNotifications
        : generalNotifications;

  function resolveLink(notification: (typeof notifications)[number]): string | undefined {
    const metadata = notification.metadata ?? {};
    const postId = typeof metadata.postId === "string" ? metadata.postId : undefined;
    const commentId = typeof metadata.commentId === "string" ? metadata.commentId : undefined;

    // Feed-app notifications: always build the deep link from metadata so legacy rows
    // (with link === "/feed") still hoist + flash the right post.
    if (notification.appId === "feed" && postId) {
      const params = new URLSearchParams({ post: postId });
      if (commentId) params.set("comment", commentId);
      return `/feed?${params.toString()}`;
    }

    return notification.link || undefined;
  }

  function openNotification(notification: (typeof notifications)[number]) {
    if (!notification.read) {
      void markAsRead(notification.id);
    }
    const link = resolveLink(notification);
    if (link) {
      setOpen(false);
      navigate(link);
    }
  }

  async function respondToFriendRequest(
    notificationId: string,
    requesterId: string,
    action: "accept" | "deny"
  ) {
    setRespondingRequestId(notificationId);
    try {
      if (action === "accept") {
        await acceptFriendRequest(requesterId);
      } else {
        await denyFriendRequest(requesterId);
      }
      await markAsRead(notificationId);
    } finally {
      setRespondingRequestId(null);
    }
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      <div
        className={cn(
          "fixed right-0 top-0 z-50 flex w-full flex-col bg-[#0b0d0f] text-[#e7e9ea] shadow-2xl transition-transform duration-300 bottom-14 md:bottom-0 md:h-full md:w-[420px] md:border-l md:border-[#191c20]",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div
          className="flex items-center justify-between border-b border-[#191c20] px-4 py-3"
          style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
        >
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#800020] to-[#c83050] text-white shadow-sm">
              <Inbox className="h-4 w-4" />
            </span>
            <h2 className="text-lg font-black tracking-tight text-[#e7e9ea]">Inbox</h2>
          </div>
          <div className="flex items-center gap-1">
            {notifications.some((n) => !n.read) && (
              <button
                onClick={() => void markAllAsRead()}
                className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs font-bold text-[#d7dbdc] transition hover:border-[#ff6b8f] hover:text-[#ff6b8f]"
                aria-label="Clear inbox"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-[#71767b] transition hover:bg-white/5 hover:text-[#ff6b8f]"
              aria-label="Close inbox"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="sticky top-0 z-10 bg-[rgba(11,13,15,0.92)] backdrop-blur-md">
          <div className="grid grid-cols-3 border-b border-[#191c20]">
            {[
              {
                id: "for-you" as const,
                label: "For You",
                icon: Sparkles,
                count: forYouNotifications.filter((n) => !n.read).length,
              },
              {
                id: "mentions" as const,
                label: "Mentions",
                icon: AtSign,
                count: mentionsNotifications.filter((n) => !n.read).length,
              },
              {
                id: "general" as const,
                label: "General",
                icon: Bell,
                count: generalNotifications.filter((n) => !n.read).length,
              },
            ].map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="relative flex items-center justify-center gap-2 py-3 text-sm font-bold transition hover:bg-white/[0.03]"
                >
                  <Icon
                    className={cn(
                      "h-4 w-4 transition",
                      active ? "text-[#ff6b8f]" : "text-[#71767b]"
                    )}
                  />
                  <span className={active ? "text-[#e7e9ea]" : "text-[#71767b]"}>
                    {tab.label}
                  </span>
                  {tab.count > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#ff477e] px-1 text-[10px] font-black text-white">
                      {tab.count > 99 ? "99+" : tab.count}
                    </span>
                  )}
                  {active && (
                    <span className="absolute bottom-0 left-1/2 h-1 w-16 -translate-x-1/2 rounded-full bg-[#ff6b8f]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {showPermissionBanner && (
          <div className="border-b border-[#191c20] bg-gradient-to-b from-[#ff6b8f]/[0.08] to-transparent px-4 py-3">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#16181c] text-[#ff6b8f]">
                <BellRing className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-[#e7e9ea]">Turn on push notifications</p>
                <p className="text-xs text-[#71767b]">Get notified on your OS even when Slumbr isn't focused.</p>
              </div>
              <button
                type="button"
                onClick={() => void enableNotifications()}
                className="shrink-0 rounded-full bg-[#c83050] px-3 py-1.5 text-xs font-black text-white transition hover:bg-[#d63b5d]"
              >
                Enable
              </button>
              <button
                type="button"
                onClick={dismissBanner}
                className="shrink-0 rounded-full p-1 text-[#71767b] transition hover:bg-white/5 hover:text-[#e7e9ea]"
                aria-label="Dismiss notification prompt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {visibleNotifications.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center px-8 py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#16181c] text-[#ff6b8f]">
                <Inbox className="h-6 w-6" />
              </div>
              <p className="text-base font-black text-[#e7e9ea]">
                No {activeTab === "for-you" ? "For You" : activeTab === "mentions" ? "Mentions" : "General"} notifications
              </p>
              <p className="mt-1 text-sm text-[#71767b]">
                You're all caught up.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[#191c20]">
              {visibleNotifications.map((notification) => {
                const requesterId =
                  typeof notification.metadata?.requesterId === "string"
                    ? notification.metadata.requesterId
                    : null;
                const isFriendRequest =
                  notification.type === "friend_request" && requesterId;
                const actor = extractActor(notification);
                const actorName = actor?.displayName ?? "S";

                return (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => {
                      if (isFriendRequest) return;
                      openNotification(notification);
                    }}
                    className={cn(
                      "flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-white/[0.025]",
                      !notification.read && "bg-[#ff6b8f]/[0.04]",
                      isFriendRequest && "cursor-default hover:bg-transparent"
                    )}
                  >
                    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#1a1d22] text-xs font-bold text-[#ff8fac]">
                      {actor?.avatarUrl ? (
                        <img
                          src={actor.avatarUrl}
                          alt={actorName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        getInitials(actorName)
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <p className="truncate text-sm font-bold text-[#e7e9ea]">
                          {notification.title}
                        </p>
                        <span className="ml-auto shrink-0 text-[11px] text-[#71767b]">
                          {formatRelative(notification.createdAt)}
                        </span>
                      </div>
                      {notification.body && (
                        <p className="mt-0.5 line-clamp-2 text-sm leading-5 text-[#a7adb2]">
                          {notification.body}
                        </p>
                      )}
                      {isFriendRequest && !notification.read && (
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              respondToFriendRequest(
                                notification.id,
                                requesterId,
                                "accept"
                              );
                            }}
                            disabled={respondingRequestId === notification.id}
                            className="rounded-full bg-[#c83050] px-3 py-1.5 text-xs font-black text-white transition hover:bg-[#d63b5d] disabled:opacity-60"
                          >
                            Accept
                          </button>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              respondToFriendRequest(
                                notification.id,
                                requesterId,
                                "deny"
                              );
                            }}
                            disabled={respondingRequestId === notification.id}
                            className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-black text-[#d7dbdc] transition hover:border-[#ff6b8f] hover:text-[#ff6b8f] disabled:opacity-60"
                          >
                            Deny
                          </button>
                        </div>
                      )}
                    </div>
                    {!notification.read && (
                      <span
                        className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-[#ff6b8f] shadow-[0_0_10px_#ff6b8f80]"
                        title="Unread"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
